import { useContext, useEffect, useRef, useState } from "react";
import type { GemInclusionParams, GroupMaterialParams } from "../../../../shared/api-contract";
import type { BindingCtx, DomainRead } from "../../../../state/editor/types";
import { useDomainState, useUi } from "../../../../state/editor/useEditor";
import { Icon } from "../../../kit";
import { useCaps } from "../../CapsProvider";
import { CompactCheck, CompactColor, CompactSlider, EditScope, Row, TextActionInput } from "../../inspector/rows";
import { LibraryGrid, type LibItem } from "../../library/LibraryGrid";
import { useManifest, type ManifestEntry } from "../../library/useManifest";

/**
 * Gem inclusions — iJewel's "Inclusions" panel (webgi's GemRefraction model,
 * contract v62), with our v32 speckle layer kept underneath as its own block.
 *
 * The two are different layers in the viewer and can both be on:
 *   - the webgi layer reads interior maps that MULTIPLY the light (white is
 *     clear, dark is a flaw), an interior normal map, an interior roughness
 *     map, and a surface layer that bends and roughens the crown reflection;
 *   - the speckle layer ADDS bright flecks from our own generated maps.
 * `scale` (speckle) and `inclusionScale` (webgi) are one tiling in the viewer,
 * so both rows read and write the same value here.
 *
 * The panel never keeps its own idea of the stone: it is seeded from
 * `getGroupGemInclusions` (every field since v62) whenever the selection or
 * the selected part's material changes — applying an iJewel inclusion `.dmat`
 * replaces the stone's inclusions, and the panel follows.
 */

/** Every field the panel shows. There is no separate `scale`: it IS `inclusionScale`. */
type Inclusions = Required<Omit<GemInclusionParams, "scale" | "color">> & { color: string };

/** The viewer's defaults (`GEM_INCLUSION_DEFAULTS`) — webgi's own for its layer. */
const DEFAULTS: Inclusions = {
  enabled: false,
  color: "#ffffff",
  bumpScale: 1,
  mapUrl: "",
  inclusionScale: 1,
  inclusionMapUrl: "",
  inclusionNormalMapUrl: "",
  incNormalScale: 0,
  inclusionRoughnessMapUrl: "",
  surfaceScale: 1,
  normalMapUrl: "",
  surfaceBumpScale: 0.1,
  surfaceRoughness: 0,
  roughnessMapUrl: "",
  blurRadius: 0.1,
  variableBlur: 0,
  variableParam1: 1,
  variableParam2: 1,
  clampHDR: false,
  colorMapUrl: "",
  colorMapScale: 1,
};

type NumField =
  | "variableBlur" | "variableParam1" | "variableParam2" | "blurRadius" | "surfaceScale"
  | "colorMapScale" | "surfaceBumpScale" | "inclusionScale" | "incNormalScale" | "surfaceRoughness";
type MapField =
  | "inclusionRoughnessMapUrl" | "inclusionMapUrl" | "roughnessMapUrl"
  | "normalMapUrl" | "inclusionNormalMapUrl" | "colorMapUrl";
const MAP_FIELDS: MapField[] = [
  "inclusionRoughnessMapUrl", "inclusionMapUrl", "roughnessMapUrl", "normalMapUrl", "inclusionNormalMapUrl", "colorMapUrl",
];

/** A map's family, as `scripts/ijewel/gen-inclusion-maps.mjs` measured it from
 *  the slots iJewel's own library binds it to. */
type MapFamily = "normal" | "grunge" | "color";

/** One row of `public/gem-inclusions/ijewel/manifest.json`. */
interface IjewelMap extends ManifestEntry {
  kind: MapFamily;
}

/** One row of `public/gem-inclusions/manifest.json` (scripts/gen-inclusions.mjs).
 *  `clean` marks the all-black flawless tile, which the grid leaves out — its
 *  "None" card is the honest off state. */
interface SpeckleMap extends ManifestEntry {
  clean?: boolean;
}

const hex = (c: string | number | undefined) =>
  typeof c === "number" ? `#${c.toString(16).padStart(6, "0")}` : (c ?? DEFAULTS.color);

/** The viewer's state, every field filled in (a pre-v62 viewer reports only the speckle ones). */
function fromViewer(st: GemInclusionParams): Inclusions {
  const out: Record<string, unknown> = { ...DEFAULTS };
  for (const key of Object.keys(DEFAULTS)) {
    const v = st[key as keyof GemInclusionParams];
    if (v !== undefined && v !== null) out[key] = v;
  }
  const s = out as Inclusions;
  s.inclusionScale = st.inclusionScale ?? st.scale ?? DEFAULTS.inclusionScale;
  s.color = hex(st.color);
  return s;
}

/** Fold a patch into the panel's copy; `scale` is `inclusionScale`. */
function merged(s: Inclusions, p: GemInclusionParams): Inclusions {
  const next = { ...s } as Record<string, unknown>;
  for (const [k, v] of Object.entries(p)) if (v !== undefined && k !== "scale") next[k] = v;
  if (p.scale !== undefined && p.inclusionScale === undefined) next.inclusionScale = p.scale;
  if (p.color !== undefined) next.color = hex(p.color);
  return next as Inclusions;
}

/**
 * Would the stone show anything? The viewer's own test: a bound map, surface
 * roughness, or variable blur at its 0.01 switch. It refuses to turn
 * inclusions ON without one — and an on-but-empty stone is a lie in a saved
 * scene — so the panel switches them off when the last of these goes.
 */
const shows = (s: Inclusions) =>
  !!s.mapUrl || MAP_FIELDS.some((f) => !!s[f]) || s.surfaceRoughness > 0 || s.variableBlur >= 0.01;

const errText = (e: unknown) => (e instanceof Error && e.message ? e.message : "Inclusions could not be applied.");
const baseName = (u: string) => decodeURIComponent(u.split(/[?#]/)[0].split("/").pop() ?? "");

const isGem = (read: DomainRead) => (read("material") as GroupMaterialParams | null)?.kind === "gem";

export function GemInclusionsControl({ ctx, read }: { ctx: BindingCtx; read: DomainRead }) {
  const caps = useCaps();
  const [ui] = useUi();
  const scope = useContext(EditScope);
  const ring = ctx.ring;
  const groupId = useDomainState("selection") as string | null;
  // Re-seeds after a material file lands on the part: a `.dmat` replaces its inclusions.
  const material = useDomainState("material");
  const speckle = useManifest<SpeckleMap>("/gem-inclusions/manifest.json");
  const ijewel = useManifest<IjewelMap>("/gem-inclusions/ijewel/manifest.json");

  const [state, setStateRaw] = useState<Inclusions>(DEFAULTS);
  const stateRef = useRef(state);
  const setState = (s: Inclusions) => {
    stateRef.current = s;
    setStateRaw(s);
  };
  const groupRef = useRef(groupId);
  groupRef.current = groupId;
  const [openMap, setOpenMap] = useState<MapField | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resync = (gid: string) => {
    if (!caps.v45) return Promise.resolve();
    return ring.getGroupGemInclusions(gid).then(
      (st) => { if (groupRef.current === gid) setState(st ? fromViewer(st) : DEFAULTS); },
      () => {},
    );
  };

  useEffect(() => {
    setError(null);
    if (!groupId || !caps.v45) {
      setState(DEFAULTS);
      return;
    }
    void resync(groupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, ring, caps.v45, material]);
  useEffect(() => setOpenMap(null), [groupId]);

  if (!caps.v32) return null;
  // Not `null`: the row is reachable by search, and an empty force-opened
  // section reads as "broken" rather than "pick a stone first".
  if (!isGem(read) || !groupId) {
    return (
      <div className="flex flex-col">
        <div className="mt-1 px-3 py-1 text-[12px] font-semibold text-ed-text-2">Inclusions</div>
        <div className="px-3 pb-1.5 text-[12px] text-ed-text-3">Select a stone to add inclusions.</div>
      </div>
    );
  }
  const webgi = caps.version >= 62;
  const speckles = speckle.items.filter((m) => !m.clean);

  /**
   * Send a patch to a stone. Only the keys that changed travel: re-sending a
   * map URL on every slider tick made the viewer dispose and re-download it
   * mid-drag. Resolves false (and resyncs from the viewer) when the viewer
   * refuses — a map that does not decode, or nothing left to show.
   */
  const write = async (p: GemInclusionParams, gid = groupId): Promise<boolean> => {
    const here = gid === groupRef.current;
    let out = p;
    if (here) {
      const next = merged(stateRef.current, p);
      if (next.enabled && !shows(next) && p.enabled === undefined) out = { ...p, enabled: false };
      setState(merged(stateRef.current, out));
      setError(null);
    }
    try {
      await ring.setGroupGemInclusions(gid, out);
      return true;
    } catch (e) {
      if (gid === groupRef.current) {
        setError(errText(e));
        await resync(gid);
      }
      return false;
    }
  };

  /** A map choice: spins its row, turns inclusions on with it (a map you
   *  cannot see is not a choice), and joins the studio's Undo history pinned
   *  to THIS stone. */
  const pick = async (field: MapField | "mapUrl", url: string, label: string) => {
    const gid = groupId;
    const before: GemInclusionParams = { [field]: stateRef.current[field], enabled: stateRef.current.enabled };
    const after: GemInclusionParams = url ? { [field]: url, enabled: true } : { [field]: "" };
    setBusy(field);
    const ok = await write(after, gid);
    setBusy(null);
    if (ok) {
      scope?.record({
        label: `${scope.section} · ${label}`,
        undo: async () => { await write(before, gid); },
        redo: async () => { await write(after, gid); },
      });
    }
    return ok;
  };

  const setEnabled = (on: boolean) => {
    if (!on || shows(stateRef.current)) return void write({ enabled: on });
    // Nothing to show yet. The seed is the speckle `cloud`, the most visible of
    // our five: measured over a converged frame it moves 31,071 px (meanΔ 12.86)
    // where `pinpoint` moves 16,642 px (meanΔ 1.50). Seeding the faintest one
    // got the whole feature reported as dead.
    const seed = speckles.find((m) => m.id === "cloud") ?? speckles[0];
    if (!seed) return setError("Choose a map first: there is nothing for inclusions to show yet.");
    void write({ enabled: true, mapUrl: speckle.urlFor(seed) });
  };

  const speckleId = state.mapUrl ? (speckles.find((m) => speckle.urlFor(m) === state.mapUrl)?.id ?? null) : null;
  /** The URL a library map travels as: its iJewel original. The viewer serves
   *  every mirrored iJewel texture from its own origin at load time, so a saved
   *  scene or an exported `.dmat` names the canonical file, never a host. */
  const sendUrl = (m: IjewelMap) => m.source ?? ijewel.urlFor(m);
  const mapOf = (url: string) =>
    url ? ijewel.items.find((m) => url === m.source || url === ijewel.urlFor(m) || baseName(url) === baseName(m.file ?? "")) : undefined;

  /** Props for a slider / switch row over one field. */
  const num = (field: NumField) => ({ value: state[field], onChange: (v: number) => void write({ [field]: v }) });
  /** Props for a map row over one field. */
  const map = (field: MapField, label: string, family: MapFamily) => ({
    field,
    value: state[field],
    current: mapOf(state[field]),
    maps: ijewel.items.filter((m) => m.kind === family),
    thumbFor: ijewel.thumbFor,
    sendUrl,
    open: openMap === field,
    busy: busy === field,
    disabled: busy !== null,
    onToggle: () => setOpenMap(openMap === field ? null : field),
    onPick: (url: string) => pick(field, url, label),
  });

  // iJewel's panel, row for row and in its order. The labels are its own row
  // names, verbatim (search reads them); the ranges are webgi 0.17's
  // (`GemRefractionPlugin.getUiConfig`: [0,1] or [0,10]). "Map" and "Color Map
  // Scale" are webgi 0.22 rows the 0.17 bundle does not have — 0–4 is ours,
  // past which the map only repeats.
  return (
    <div className="flex flex-col" data-gem-inclusions>
      <div className="mt-1 px-3 py-1 text-[12px] font-semibold text-ed-text-2">Inclusions</div>
      <Row label="Enabled" hint="Turns this stone's inclusions on. With nothing chosen yet it starts from the Cloud speckle below.">
        <CompactCheck checked={state.enabled} onChange={setEnabled} />
      </Row>
      {webgi ? (
        <>
          <Row label="VariableBlur" hint="Patchy, view-dependent blur inside the stone. In webgi this is a switch at 0.01, not a strength.">
            <CompactSlider min={0} max={1} step={0.01} {...num("variableBlur")} />
          </Row>
          <Row label="VariableParam1" hint="Shapes the patchy-blur noise.">
            <CompactSlider min={0} max={10} step={0.01} {...num("variableParam1")} />
          </Row>
          <Row label="VariableParam2" hint="Shapes the patchy-blur noise.">
            <CompactSlider min={0} max={10} step={0.01} {...num("variableParam2")} />
          </Row>
          <Row label="blurRadius" hint="How far roughness blurs what you see through the stone.">
            <CompactSlider min={0} max={1} step={0.001} {...num("blurRadius")} />
          </Row>
          <Row label="ClampHDR" hint="Clamps the blurred interior to 1.0, so blown highlights do not smear into halos.">
            <CompactCheck checked={state.clampHDR} onChange={(v: boolean) => void write({ clampHDR: v })} />
          </Row>
          <Row label="SurfaceScale" hint="Tiling of the surface maps (normalMap, RoughnessMap).">
            <CompactSlider min={0} max={10} step={0.01} {...num("surfaceScale")} />
          </Row>
          <Row label="Color Map Scale" hint="Size of the body colour Map about the stone's centre. 1 spans the stone.">
            <CompactSlider min={0} max={4} step={0.01} {...num("colorMapScale")} />
          </Row>
          <Row label="BumpScale" hint="Strength of the surface normalMap on the crown reflection.">
            <CompactSlider min={0} max={10} step={0.001} {...num("surfaceBumpScale")} />
          </Row>
          <Row label="InclusionScale" hint="Tiling of the interior maps, and of the speckle. 1 is one tile across the stone.">
            <CompactSlider min={0} max={10} step={0.01} {...num("inclusionScale")} />
          </Row>
          <Row label="IncNormalScale" hint="Strength of the InclusionNormalMap. At 0 a chosen map leaves the facets as they are.">
            <CompactSlider min={0} max={10} step={0.01} {...num("incNormalScale")} />
          </Row>
          <MapRow
            label="InclusionRoughness"
            hint="Interior roughness: how milky the light leaving the stone looks."
            {...map("inclusionRoughnessMapUrl", "InclusionRoughness", "grunge")}
          />
          <MapRow
            label="InclusionMap"
            hint="Interior flaws. Multiplies the light: white is clear, dark is a flaw."
            {...map("inclusionMapUrl", "InclusionMap", "grunge")}
          />
          <Row label="Surface Roughness" hint="Roughness of the crown reflection. It also blurs the interior, through blurRadius.">
            <CompactSlider min={0} max={1} step={0.001} {...num("surfaceRoughness")} />
          </Row>
          <MapRow label="RoughnessMap" hint="Multiplies Surface Roughness across the crown." {...map("roughnessMapUrl", "RoughnessMap", "grunge")} />
          <MapRow label="normalMap" hint="Surface normal map. Bends the crown reflection only." {...map("normalMapUrl", "normalMap", "normal")} />
          <MapRow
            label="InclusionNormalMap"
            hint="Interior normal map. Replaces the facet normal the light refracts through."
            {...map("inclusionNormalMapUrl", "InclusionNormalMap", "normal")}
          />
          <MapRow
            label="Map"
            hint="Body colour, projected through the girdle plane: the bicolour tourmaline and ametrine stones."
            {...map("colorMapUrl", "Map", "color")}
          />
        </>
      ) : null}

      <div className="mt-2 border-t border-ed-border px-3 pb-0.5 pt-2 text-[12px] font-semibold text-ed-text-2">Speckle</div>
      <p className="px-3 pb-1 text-[12px] leading-[16px] text-ed-text-3">
        Our bright flecks, added on top of the inclusions above. Both can be on.
      </p>
      <Row label="Color">
        <CompactColor value={state.color} onChange={(v: string) => void write({ color: v })} />
      </Row>
      <Row label="Scale" hint="The same tiling as InclusionScale above.">
        <CompactSlider value={state.inclusionScale} min={0.1} max={10} step={0.01} onChange={(v: number) => void write({ scale: v })} />
      </Row>
      <Row label="Bump Scale" hint="How bright the speckle flecks are.">
        <CompactSlider value={state.bumpScale} min={0} max={4} step={0.01} onChange={(v: number) => void write({ bumpScale: v })} />
      </Row>
      {speckles.length ? (
        <div className="flex flex-col gap-1.5 px-3 py-1.5">
          <span className="text-[12px] font-medium text-ed-text-2">Inclusion Map</span>
          <LibraryGrid
            items={speckles.map((m): LibItem => ({ id: m.id, label: m.label, thumb: speckle.urlFor(m) }))}
            activeId={speckleId}
            view={ui.libraryView}
            // Returned, not `void`ed, so the tile spins for the real decode and
            // the check-mark moves only once the viewer has bound the texture.
            onSelect={(it: LibItem) => {
              const entry = speckles.find((m) => m.id === it.id);
              return entry ? pick("mapUrl", speckle.urlFor(entry), "Inclusion Map") : undefined;
            }}
            // "None" clears the speckle only; the webgi layer above keeps its maps.
            onReset={() => pick("mapUrl", "", "Inclusion Map")}
          />
          <span className="text-[11px] text-ed-text-3">Generated in-house · scripts/gen-inclusions.mjs</span>
        </div>
      ) : null}
      <Row label="Custom Map URL">
        <TextActionInput
          placeholder="https://…"
          action="Load"
          // Commit-on-submit: a per-keystroke field fired a texture load for
          // every character typed.
          onSubmit={(v: string) => pick("mapUrl", v, "Custom Map URL").then((ok) => { if (!ok) throw new Error("load failed"); })}
        />
      </Row>
      {error ? (
        <span className="px-3 pb-1.5 text-[12px] leading-[16px] text-ed-red" role="alert">{error}</span>
      ) : null}
    </div>
  );
}

/**
 * One map row: the chosen map (thumbnail + name) on the right, and on click a
 * tile strip of the mirrored iJewel maps of the family this slot takes, "None",
 * and a URL box. One strip is open at a time.
 */
function MapRow({
  label,
  hint,
  field,
  value,
  current,
  maps,
  thumbFor,
  open,
  busy,
  disabled,
  onToggle,
  onPick,
  sendUrl,
}: {
  label: string;
  hint: string;
  field: MapField;
  value: string;
  current: IjewelMap | undefined;
  maps: IjewelMap[];
  thumbFor: (m: ManifestEntry) => string;
  open: boolean;
  busy: boolean;
  disabled: boolean;
  onToggle: () => void;
  onPick: (url: string) => Promise<boolean>;
  sendUrl: (m: IjewelMap) => string;
}) {
  const name = !value ? "None" : current?.label ?? (value.startsWith("data:") ? "Embedded map" : baseName(value) || "Custom map");
  const tile = "relative aspect-square overflow-hidden rounded-md border transition-colors disabled:cursor-not-allowed disabled:opacity-50";
  return (
    <>
      <Row label={label} hint={hint}>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          title={value || "No map"}
          data-inclusion-map={field}
          className={`flex min-w-0 flex-1 items-center gap-2 rounded-md border bg-white px-1.5 py-1 text-left text-[12px] text-ed-text transition-colors ${
            open ? "border-ed-accent" : "border-ed-line hover:border-ed-accent"
          }`}
        >
          <span
            className="grid h-5 w-5 shrink-0 place-items-center overflow-hidden rounded-[4px] border border-ed-border bg-ed-panel-2 bg-cover bg-center"
            style={current ? { backgroundImage: `url("${thumbFor(current)}")` } : undefined}
          >
            {!value ? <Icon name="block" size={12} className="text-ed-text-3" /> : !current ? <Icon name="image" size={12} className="text-ed-text-3" /> : null}
          </span>
          <span className={`min-w-0 flex-1 truncate ${value ? "" : "text-ed-text-2"}`}>{name}</span>
          {busy ? (
            <Icon name="progress_activity" size={14} className="animate-spin text-ed-text-3" />
          ) : (
            <Icon name={open ? "expand_less" : "expand_more"} size={16} className="text-ed-text-3" />
          )}
        </button>
      </Row>
      {open ? (
        <div className="flex flex-col gap-2 px-3 pb-2 pt-0.5" data-inclusion-maps={field}>
          <div className="grid grid-cols-6 gap-1.5">
            <button
              type="button"
              title="None"
              aria-pressed={!value}
              disabled={disabled}
              onClick={() => void onPick("")}
              className={`${tile} grid place-items-center bg-white text-ed-text-3 ${!value ? "border-ed-accent ring-2 ring-ed-accent/40" : "border-ed-border hover:border-ed-accent"}`}
            >
              <Icon name="block" size={16} />
            </button>
            {maps.map((m) => {
              const on = current?.id === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  title={m.label}
                  aria-label={m.label}
                  aria-pressed={on}
                  disabled={disabled}
                  onClick={() => void onPick(sendUrl(m))}
                  data-inclusion-map-item={m.id}
                  className={`${tile} bg-ed-panel-2 bg-cover bg-center ${on ? "border-ed-accent ring-2 ring-ed-accent/40" : "border-ed-border hover:border-ed-accent"}`}
                  style={{ backgroundImage: `url("${thumbFor(m)}")` }}
                />
              );
            })}
          </div>
          <TextActionInput
            placeholder="Map URL…"
            action="Load"
            onSubmit={(v: string) => onPick(v).then((ok) => { if (!ok) throw new Error("load failed"); })}
          />
        </div>
      ) : null}
    </>
  );
}
